import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, FormControl, Grid, MenuItem, Select } from '@mui/material';

const generateYearOptions = () => {
  const currentYear = moment().year();
  const years = [];

  // Generate last 5 years including current year (2 years total)
  for (let year = currentYear - 1; year <= currentYear; year++) {
    years.push(
      <MenuItem key={year} value={year.toString()}>
        {year}
      </MenuItem>
    );
  }

  return years;
};

export function useDateRangeFilter(fiscalYearOnly = false, payperiod, allowFuture = false) {
  const [params, setParams] = useSearchParams();
  const [dateRange, setDateRange] = useState(() =>
    fiscalYearOnly ? 'fiscalYear' : 7
  );
  const isCustomDateRange = dateRange !== 'custom';
  const [fromError, setFromError] = useState('');
  const [toError, setToError] = useState('');

  const fromDateParam = params.get('fromDate');
  const toDateParam = params.get('toDate');

  const getFiscalYearRange = () => {
    const today = moment();
    const year = today.month() >= 6 ? today.year() : today.year() - 1;

    const start = moment(`${year}-07-01`).startOf('day').toDate();
    const end = moment(`${year + 1}-06-30`)
      .endOf('day')
      .toDate();

    return { start, end };
  };

  const fromDate = useMemo(() => {
    if (fromDateParam) return new Date(fromDateParam);

    if (fiscalYearOnly) {
      return getFiscalYearRange().start;
    }

    return moment().subtract(7, 'days').toDate();
  }, [fromDateParam, fiscalYearOnly]);

  const toDate = useMemo(() => {
    if (toDateParam) if (toDateParam) return new Date(toDateParam);

    if (fiscalYearOnly) {
      return getFiscalYearRange().end;
    }

    return new Date();
  }, [toDateParam, fiscalYearOnly]);

  const isInvalidTimerange = useMemo(
    () => new Date(fromDate) > new Date(toDate),
    [fromDate, toDate]
  );

  const handleChangeFromDate = useCallback(
    (value) => {
      setDateRange('custom');
      const isInvalid = !value || value.toString() === 'Invalid Date';
      const isInvalidTime = new Date(value) > new Date(toDate);
      if (isInvalid) {
        setFromError('Invalid start date');
      }
      if (toDate && isInvalidTime) {
        setFromError('Start date cannot be after end date');
      }
      if (!isInvalidTime && fromError) setFromError('');
      setParams((prev) => {
        prev.set('fromDate', isInvalid ? value : value.toISOString());
        return prev;
      });
    },
    [setParams, toDate, fromError]
  );

  const handleChangeToDate = useCallback(
    (value) => {
      setDateRange('custom');
      const isInvalid = !value || value.toString() === 'Invalid Date';
      const isInvalidTime = new Date(value) < new Date(fromDate);
      if (isInvalid) {
        setToError('Invalid end date');
      }
      if (fromDate && isInvalidTime) {
        setToError('End date cannot be before start date');
      }
      if (!isInvalidTime && toError) setToError('');
      setParams((prev) => {
        prev.set('toDate', isInvalid ? value : value.toISOString());
        return prev;
      });
    },
    [setParams, fromDate, toError]
  );

  const handleChangePeriod = useCallback(
    (e) => {
      const value = e.target.value;
      setDateRange(value);

      if (value && value !== 'custom') {
        setParams((prev) => {
          const changeParams = ({ start_dt, end_dt }) => {
            prev.set('fromDate', start_dt.toDate().toISOString());
            prev.set('toDate', end_dt.toDate().toISOString());
          };

          if (typeof value === 'number') {
            const start_dt = moment().subtract(value, 'days');
            const end_dt = moment();
            changeParams({ start_dt, end_dt });
            return prev;
          }

          switch (value) {
            case 'today': {
              const start_dt = moment().startOf('day');
              const end_dt = moment();
              changeParams({ start_dt, end_dt });
              break;
            }
            case 'tomorrow': {
              const start_dt = moment().add(1, 'day').startOf('day');
              const end_dt = moment().add(1, 'day').endOf('day');
              changeParams({ start_dt, end_dt });
              break;
            }
            case 'mtd': {
              const start_dt = moment().startOf('month');
              const end_dt = moment();
              changeParams({ start_dt, end_dt });
              break;
            }
            case 'ytd': {
              const start_dt = moment().startOf('year');
              const end_dt = moment();
              changeParams({ start_dt, end_dt });
              break;
            }
            case 'fiscalYear': {
              const { start_dt, end_dt } = getFiscalYearRange();
              changeParams({ start_dt, end_dt });
              break;
            }
            case 'lastPay': {
              if (payperiod) {
                const start_dt = moment(payperiod.last_pay_period_start_date);
                const end_dt = moment(payperiod.last_pay_period_end_date);
                changeParams({ start_dt, end_dt });
              }
              break;
            }
            default: {
              // year values
              const yearMatch = value.match(/^\d{4}$/);
              if (yearMatch) {
                const year = parseInt(value);
                const start_dt = moment(`${year}-01-01`).startOf('day');
                const end_dt = moment(`${year}-12-31`).endOf('day');
                changeParams({ start_dt, end_dt });
              }
              break;
            }
          }

          return prev;
        });
      }
    },
    [payperiod, setParams]
  );

  const menuItems = useMemo(() => {
    if (fiscalYearOnly) {
      return [
        { value: 'custom', label: 'Custom range' },
        { value: 'fiscalYear', label: 'Current fiscal year' },
      ];
    } else {
      const items = [
        { value: 'custom', label: 'Custom range' },
        { value: 7, label: 'Last 7 days' },
        { value: 30, label: 'Last 30 days' },
        { value: 365, label: 'Last year' },
        { value: 'today', label: 'Today' },
        { value: 'tomorrow', label: 'Tomorrow' },
        { value: 'mtd', label: 'Month to date' },
        { value: 'ytd', label: 'Year to date' },
      ];
      if (payperiod) {
        items.push({ value: 'lastPay', label: 'Last pay period ' });
      }
      return items;
    }
  }, [fiscalYearOnly, payperiod]);

  const render = (
    <>
      <Grid item xs={12} md={4}>
        <Box sx={{ position: 'relative' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={fromDate}
              onChange={handleChangeFromDate}
              maxDate={toDate}
              disabled={isCustomDateRange}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  height: '36px',
                  fontSize: '14px',
                },
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  label: 'Start date',
                  error: Boolean(fromError) || isInvalidTimerange,
                  helperText: isInvalidTimerange
                    ? fromError || 'Start date cannot be after end date'
                    : '',
                  FormHelperTextProps: {
                    sx: {
                      position: 'absolute',
                      left: 0,
                      bottom: '-60%',
                      m: 0,
                      fontSize: '12px',
                      textWrap: 'nowrap',
                    },
                  },
                },
              }}
              slots={{ OpenPickerIcon: CalendarTodayIcon }}
            />
          </LocalizationProvider>
        </Box>
      </Grid>
      <Grid item xs={12} md={4}>
        <Box sx={{ position: 'relative' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={toDate}
              onChange={handleChangeToDate}
              disableFuture={!allowFuture}
              minDate={fromDate}
              disabled={isCustomDateRange}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  height: '36px',
                  fontSize: '14px',
                },
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  label: 'End date',
                  error: Boolean(toError) || isInvalidTimerange,
                  helperText: isInvalidTimerange
                    ? toError
                      ? toError
                      : 'End date cannot be before start date'
                    : '',
                  FormHelperTextProps: {
                    sx: {
                      position: 'absolute',
                      left: 0,
                      bottom: '-60%',
                      m: 0,
                      fontSize: '12px',
                      textWrap: 'nowrap',
                    },
                  },
                },
              }}
              slots={{ OpenPickerIcon: CalendarTodayIcon }}
            />
          </LocalizationProvider>
        </Box>
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControl sx={{ width: '100%' }} size="small">
          <Select
            sx={{ width: '100%', height: '36px' }}
            id="Period-selection"
            value={dateRange}
            onChange={handleChangePeriod}
          >
            {menuItems.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
            {!fiscalYearOnly && generateYearOptions()}
          </Select>
        </FormControl>
      </Grid>
    </>
  );

  return { fromDate, toDate, render, dateRange };
}
